import React, { useEffect, useMemo, useState, type FC } from 'react'

import { useIntl } from 'react-intl'

import { useRecoilCallback, useRecoilValue } from 'recoil'

import { Flex, FormElement, Select } from 'mailup-web-components'

import { INPUT_MESSAGES_MAX_ITEMS } from 'Constants'

import type { SearchResult } from 'Pages/RecipientsList/model'

import GroupsList from 'Pages/RecipientsList/components/aside/groups/GroupsList'

import useAsyncSelect from 'Hooks/useAsyncSelect/useAsyncSelect'

import {
  fetchGroupById,
  fetchGroups,
} from 'Pages/RecipientsList/recoil/services/asyncSelectGroups'

import {
  addRecipientToGroupCallback,
  addRecipientToNewGroupCallback,
  initGroupsCallback,
  resetGroupsCallback,
} from 'Pages/RecipientsList/recoil/callbacks/groups'

import { recipientAllGroupsState } from 'Pages/RecipientsList/recoil/state/recipient'

interface TabGroupsProps {
  item: SearchResult
}

const TabGroups: FC<TabGroupsProps> = ({ item }) => {
  const intl = useIntl()

  const [forceCollapse, setForceCollapse] = useState(false)

  const allRecipientGroups = useRecoilValue(recipientAllGroupsState)

  const initGroups = useRecoilCallback(initGroupsCallback, [])
  const resetGroups = useRecoilCallback(resetGroupsCallback, [])
  const addRecipientToGroup = useRecoilCallback(addRecipientToGroupCallback, [])
  const addRecipientToNewGroup = useRecoilCallback(
    addRecipientToNewGroupCallback,
    [],
  )

  const { options, loading, handleSearch, hint, hintNoResults, updateOptions } =
    useAsyncSelect({
      key: 'recipient-groups',
      value: undefined,
      maxItems: INPUT_MESSAGES_MAX_ITEMS,
      fetchItems: async () => await fetchGroups(''),
      fetchItemsByTerm: fetchGroups,
      fetchItemById: fetchGroupById,
      labels: {
        loading: intl.formatMessage({
          id: 'Load.MailupCommon.InputStatusLoading',
        }),
        noResults: intl.formatMessage({
          id: 'Load.MailupCommon.InputStatusNoMatches',
        }),
        moreResults: intl.formatMessage({
          id: 'Load.MailupCommon.InputStatusTooManyItems',
        }),
      },
    })

  const handleSelectGroupChange = (v: number | undefined): void => {
    if (v !== undefined) {
      void addRecipientToGroup(item.Id, v)
    }
  }

  const handleSelectGroupExpandCollapse = (c: boolean): void => {
    if (!c) {
      handleSearch('')
    }
  }

  const handleSelectGroupSubmit = async (s: string): Promise<void> => {
    const newGroupId = await addRecipientToNewGroup(item.Id, s)

    if (newGroupId !== undefined) {
      void updateOptions(newGroupId)
    }

    setForceCollapse(true)

    setTimeout(() => {
      setForceCollapse(false)
    }, 250)
  }

  useEffect(() => {
    void initGroups(item.Id)

    return () => {
      void resetGroups()
    }
  }, [item.Id])

  const decoratedOptions = useMemo(() => {
    return options.map((o: { value: number }) => ({
      ...o,
      disabled: allRecipientGroups.includes(o.value),
    }))
  }, [options, allRecipientGroups])

  return (
    <Flex flexDirection="column" gap="24px" margin="24px 0 0 0">
      <FormElement
        label={intl.formatMessage({
          id: 'Load.RecipientsList.AddToGroupsLabel',
        })}
        hint={
          <span>
            {intl.formatMessage(
              { id: 'Load.RecipientsList.AddToGroupsHint' },
              { strong: (chunks) => <strong>{chunks}</strong> },
            )}
          </span>
        }
      >
        <Select
          value={undefined}
          options={decoratedOptions}
          loading={loading}
          onChange={handleSelectGroupChange}
          onSearch={handleSearch}
          onSubmit={handleSelectGroupSubmit}
          onExpandCollapse={handleSelectGroupExpandCollapse}
          hint={hint}
          hintNoResults={hintNoResults}
          memoSelectOption
          placeholder={intl.formatMessage({
            id: 'Load.RecipientsList.SelectOrAddGroupPlaceholder',
          })}
          forceCollapse={forceCollapse}
          data-mu-test="input-select-group-to-add"
        />
      </FormElement>

      <GroupsList idRecipient={item.Id} />
    </Flex>
  )
}

export default TabGroups
